import { BaseGrid } from './BaseGrid';
import { Cell, CellType } from './Cell';
import { GridType } from './GridType';
import { Point2D, CubicBezierCurve, evaluateCoonsPatch } from './BezierMath';
import { BezierLayoutPreset, generateLayout } from './BezierLayoutPresets';

export interface CurveControlPoints {
  cp1: Point2D;
  cp2: Point2D;
}

export interface BezierGridConfig {
  patchRows: number;
  patchCols: number;
  patchCountX: number;
  patchCountY: number;
  canvasWidth: number;
  canvasHeight: number;
  preset?: BezierLayoutPreset;
  activePatchMask?: boolean[][];
}

export class BezierGrid extends BaseGrid {
  public readonly canvasWidth: number;
  public readonly canvasHeight: number;
  public readonly patchRows: number;
  public readonly patchCols: number;
  public readonly patchCountX: number;
  public readonly patchCountY: number;
  public readonly preset: BezierLayoutPreset;
  private readonly activePatchMask: boolean[][] | null;

  private corners: Point2D[][] = [];
  private hCurves: CurveControlPoints[][] = [];
  private vCurves: CurveControlPoints[][] = [];
  private vertices: Point2D[][] = [];

  constructor(config: BezierGridConfig) {
    super({
      rows: config.patchRows * config.patchCountY,
      cols: config.patchCols * config.patchCountX,
      gridType: GridType.BEZIER
    });
    this.canvasWidth = config.canvasWidth;
    this.canvasHeight = config.canvasHeight;
    this.patchRows = config.patchRows;
    this.patchCols = config.patchCols;
    this.patchCountX = config.patchCountX;
    this.patchCountY = config.patchCountY;
    this.preset = config.preset ?? 'island';
    this.activePatchMask = config.activePatchMask ?? null;

    this.initDefaultCurves();
    this.recomputeVertices();
    this.markInactivePatches();
  }

  private initDefaultCurves(): void {
    const layout = generateLayout(
      this.preset,
      this.canvasWidth,
      this.canvasHeight,
      this.patchCountX,
      this.patchCountY
    );
    this.corners = layout.corners;
    this.hCurves = layout.hCurves;
    this.vCurves = layout.vCurves;
  }

  private markInactivePatches(): void {
    if (!this.activePatchMask) return;
    for (let py = 0; py < this.patchCountY; py++) {
      for (let px = 0; px < this.patchCountX; px++) {
        if (this.activePatchMask[py]?.[px] === false) {
          const rowStart = py * this.patchRows;
          const colStart = px * this.patchCols;
          for (let r = rowStart; r < rowStart + this.patchRows; r++) {
            for (let c = colStart; c < colStart + this.patchCols; c++) {
              const cell = this.getCell(r, c);
              if (cell) cell.type = CellType.WALL;
            }
          }
        }
      }
    }
  }

  isCellActive(row: number, col: number): boolean {
    if (!this.activePatchMask) return true;
    const py = Math.min(Math.floor(row / this.patchRows), this.patchCountY - 1);
    const px = Math.min(Math.floor(col / this.patchCols), this.patchCountX - 1);
    return this.activePatchMask[py]?.[px] !== false;
  }

  recomputeVertices(): void {
    const totalRows = this.rows;
    const totalCols = this.cols;
    this.vertices = [];

    for (let r = 0; r <= totalRows; r++) {
      this.vertices[r] = [];
      for (let c = 0; c <= totalCols; c++) {
        const patchY = Math.min(Math.floor(r / this.patchRows), this.patchCountY - 1);
        const patchX = Math.min(Math.floor(c / this.patchCols), this.patchCountX - 1);
        const localR = r - patchY * this.patchRows;
        const localC = c - patchX * this.patchCols;
        const v = localR / this.patchRows;
        const u = localC / this.patchCols;

        const top = this.buildCurve(
          this.corners[patchY][patchX],
          this.hCurves[patchY][patchX],
          this.corners[patchY][patchX + 1]
        );
        const bottom = this.buildCurve(
          this.corners[patchY + 1][patchX],
          this.hCurves[patchY + 1][patchX],
          this.corners[patchY + 1][patchX + 1]
        );
        const left = this.buildCurve(
          this.corners[patchY][patchX],
          this.vCurves[patchY][patchX],
          this.corners[patchY + 1][patchX]
        );
        const right = this.buildCurve(
          this.corners[patchY][patchX + 1],
          this.vCurves[patchY][patchX + 1],
          this.corners[patchY + 1][patchX + 1]
        );

        this.vertices[r][c] = evaluateCoonsPatch(u, v, top, bottom, left, right);
      }
    }
  }

  private buildCurve(start: Point2D, cps: CurveControlPoints, end: Point2D): CubicBezierCurve {
    return { p0: start, p1: cps.cp1, p2: cps.cp2, p3: end };
  }

  getQuadCorners(row: number, col: number): [Point2D, Point2D, Point2D, Point2D] {
    return [
      this.vertices[row][col],
      this.vertices[row][col + 1],
      this.vertices[row + 1][col + 1],
      this.vertices[row + 1][col]
    ];
  }

  getCellCenter(row: number, col: number): Point2D {
    const [a, b, c, d] = this.getQuadCorners(row, col);
    return {
      x: (a.x + b.x + c.x + d.x) / 4,
      y: (a.y + b.y + c.y + d.y) / 4
    };
  }

  getVertices(): Point2D[][] {
    return this.vertices;
  }

  getCorners(): Point2D[][] { return this.corners; }
  getHCurves(): CurveControlPoints[][] { return this.hCurves; }
  getVCurves(): CurveControlPoints[][] { return this.vCurves; }

  getAllDraggablePoints(): { point: Point2D; isCorner: boolean }[] {
    const result: { point: Point2D; isCorner: boolean }[] = [];

    for (let r = 0; r < this.corners.length; r++) {
      for (let c = 0; c < this.corners[r].length; c++) {
        result.push({ point: this.corners[r][c], isCorner: true });
      }
    }

    for (let r = 0; r < this.hCurves.length; r++) {
      for (let c = 0; c < this.hCurves[r].length; c++) {
        result.push({ point: this.hCurves[r][c].cp1, isCorner: false });
        result.push({ point: this.hCurves[r][c].cp2, isCorner: false });
      }
    }

    for (let r = 0; r < this.vCurves.length; r++) {
      for (let c = 0; c < this.vCurves[r].length; c++) {
        result.push({ point: this.vCurves[r][c].cp1, isCorner: false });
        result.push({ point: this.vCurves[r][c].cp2, isCorner: false });
      }
    }

    return result;
  }

  getNeighbors(cell: Cell): Cell[] {
    const neighbors: Cell[] = [];
    const directions = [[-1, 0], [1, 0], [0, -1], [0, 1]];

    for (const [dRow, dCol] of directions) {
      const newRow = cell.row + dRow;
      const newCol = cell.col + dCol;
      const neighbor = this.getCell(newRow, newCol);
      if (neighbor && neighbor.isWalkable) {
        neighbors.push(neighbor);
      }
    }

    return neighbors;
  }

  calculateDistance(cellA: Cell, cellB: Cell): number {
    const centerA = this.getCellCenter(cellA.row, cellA.col);
    const centerB = this.getCellCenter(cellB.row, cellB.col);
    const dx = centerA.x - centerB.x;
    const dy = centerA.y - centerB.y;
    return Math.sqrt(dx * dx + dy * dy);
  }
}
